// Instant quote pricing engine for IZHUB Cleaning.
// Pure functions — no side effects, safe to call on every render.

export type ServiceId =
  | "regular"
  | "one-off"
  | "deep"
  | "tenancy"
  | "military"
  | "commercial"
  | "airbnb";

export const SERVICES: { id: ServiceId; label: string; blurb: string }[] = [
  { id: "regular", label: "Regular domestic", blurb: "Weekly / fortnightly / monthly" },
  { id: "one-off", label: "One-off domestic", blurb: "Single standard clean" },
  { id: "deep", label: "Deep clean", blurb: "Top-to-bottom reset" },
  { id: "tenancy", label: "End of tenancy", blurb: "Inspection-ready checkout clean" },
  { id: "military", label: "Military march-out", blurb: "MOD inspection standard" },
  { id: "airbnb", label: "Airbnb / short-let", blurb: "Turnaround between guests" },
  { id: "commercial", label: "Commercial", blurb: "Offices & premises — custom quote" },
];

export type Bedrooms = "studio" | "1" | "2" | "3" | "4" | "5+";
export type Bathrooms = "1" | "2" | "3" | "4+";
export type Receptions = "0" | "1" | "2" | "3+";
export type KitchenSize = "small" | "medium" | "large";
export type Condition = "light" | "standard" | "heavy" | "very-heavy";
export type Frequency = "one-off" | "weekly" | "fortnightly" | "monthly" | "tenancy" | "march-out";

export const ADDONS = [
  { id: "oven", label: "Oven cleaning", amount: 45 },
  { id: "fridge", label: "Fridge / freezer", amount: 25 },
  { id: "garage", label: "Garage cleaning", amount: 20 },
  { id: "wheelie-bins", label: "Wheelie bins cleaning", amount: 10 },
  { id: "cupboards", label: "Inside cupboards", amount: 25 },
  { id: "extra-bathroom", label: "Extra bathroom", amount: 20 },
  { id: "carpets", label: "Carpet cleaning", amount: 0, ptc: true },
  { id: "after-builders", label: "After-builders dust (+20%)", amount: 0, pct: 0.2 },
] as const;

export type AddonId = (typeof ADDONS)[number]["id"];

/** Carpet cleaning guide prices shown when the carpets add-on is selected. */
export const CARPET_PRICES: { id: string; label: string; price: number }[] = [
  { id: "small-bedroom", label: "Small bedroom", price: 20 },
  { id: "standard-bedroom", label: "Standard bedroom", price: 30 },
  { id: "large-bedroom", label: "Large bedroom", price: 40 },
  { id: "dining-room", label: "Dining room", price: 25 },
  { id: "lounge", label: "Lounge", price: 50 },
  { id: "hallway", label: "Hallway", price: 20 },
  { id: "stairs", label: "Stairs", price: 30 },
  { id: "stairs-landing", label: "Stairs + landing", price: 40 },
];

/** Carpet selections as id → quantity. */
export type CarpetSelection = Record<string, number>;

export function carpetSelectionTotal(sel: CarpetSelection): number {
  return CARPET_PRICES.reduce((sum, c) => sum + c.price * (sel[c.id] ?? 0), 0);
}

export function carpetSelectionLabel(sel: CarpetSelection): string {
  return CARPET_PRICES.filter((c) => (sel[c.id] ?? 0) > 0)
    .map((c) => {
      const qty = sel[c.id]!;
      return qty > 1 ? `${c.label} × ${qty} (${formatPrice(c.price * qty)})` : `${c.label} (${formatPrice(c.price)})`;
    })
    .join(", ");
}

export interface QuoteInput {
  service: ServiceId;
  bedrooms: Bedrooms;
  bathrooms: Bathrooms;
  receptions: Receptions;
  kitchen: KitchenSize;
  condition: Condition;
  frequency: Frequency;
  addons: AddonId[];
  /** Selected carpet rooms (id → quantity from CARPET_PRICES) — priced into the estimate. */
  carpetRooms?: CarpetSelection;
  wheelieBins?: number;
}

export interface QuoteResult {
  mode: "estimate" | "from" | "custom";
  low: number;
  high: number;
  hoursLow: number;
  hoursHigh: number;
  cleaners: number;
  currency: string;
  breakdown: { label: string; amount?: number; note?: string; quantity?: number; unitPrice?: number }[];
  priceToConfirm: string[];
  notes: string[];
  disclaimer: string;
}

const HOURLY_RATES: Record<string, number> = {
  regular: 22,
  "one-off": 25,
  deep: 25,
};

const REGULAR_HOURS: Record<Bedrooms, [number, number]> = {
  studio: [2, 2],
  "1": [2, 2],
  "2": [2.5, 3],
  "3": [3, 4],
  "4": [4, 5],
  "5+": [5, 6],
};

const DEEP_HOURS: Record<Bedrooms, [number, number]> = {
  studio: [3, 4],
  "1": [3, 4],
  "2": [4, 5],
  "3": [5, 7],
  "4": [7, 9],
  "5+": [9, 11],
};

const TENANCY_FROM: Record<Bedrooms, number> = {
  studio: 160, // fallback — treated as 1BR
  "1": 160,
  "2": 190,
  "3": 240,
  "4": 300,
  "5+": 360,
};

const MILITARY_FROM: Record<Bedrooms, number> = {
  studio: 160, // fallback — spec starts at 1BR; treat studio as 1BR
  "1": 160,
  "2": 210,
  "3": 260,
  "4": 320,
  "5+": 380,
};

const AIRBNB_FROM: Record<Bedrooms, number> = {
  studio: 45,
  "1": 45,
  "2": 60,
  "3": 75,
  "4": 95,
  "5+": 120,
};

/**
 * Public "guide price" summary used on marketing pages.
 * Values are derived from the same tables the calculator uses,
 * so displayed prices can never drift from real quotes.
 */
export const GUIDE_PRICES: {
  service: ServiceId;
  label: string;
  price: string;
  unit: string;
  amount: number | null;
  includes: string;
}[] = [
  {
    service: "regular",
    label: "Domestic cleaning",
    price: `£${HOURLY_RATES.regular}`,
    unit: "per hour",
    amount: HOURLY_RATES.regular,
    includes: "Weekly or fortnightly cleans — kitchens, bathrooms, bedrooms and living areas.",
  },
  {
    service: "one-off",
    label: "One-off house clean",
    price: `£${HOURLY_RATES["one-off"]}`,
    unit: "per hour",
    amount: HOURLY_RATES["one-off"],
    includes: "A single standard clean with no ongoing commitment.",
  },
  {
    service: "deep",
    label: "Deep cleaning",
    price: `£${HOURLY_RATES.deep}`,
    unit: "per hour",
    amount: HOURLY_RATES.deep,
    includes: "Top-to-bottom reset including limescale, degreasing, skirtings and edges.",
  },
  {
    service: "tenancy",
    label: "End of tenancy cleaning",
    price: `from £${TENANCY_FROM["1"]}`,
    unit: "per property",
    amount: TENANCY_FROM["1"],
    includes: "Inspection-ready checkout clean for tenants, landlords and letting agents.",
  },
  {
    service: "military",
    label: "Military march-out cleaning",
    price: `from £${MILITARY_FROM["1"]}`,
    unit: "per property",
    amount: MILITARY_FROM["1"],
    includes: "Cleaned to MOD march-out inspection standard across Salisbury Plain.",
  },
  {
    service: "airbnb",
    label: "Airbnb & short-let turnaround",
    price: `from £${AIRBNB_FROM["1"]}`,
    unit: "per changeover",
    amount: AIRBNB_FROM["1"],
    includes: "Fast, consistent turnarounds between guests with linen change on request.",
  },
  {
    service: "commercial",
    label: "Office & commercial cleaning",
    price: "Custom quote",
    unit: "tailored to your site",
    amount: null,
    includes: "Offices, clinics, salons and studios — in-hours or out-of-hours schedules.",
  },
];

const CONDITION_MULT: Record<Condition, number> = {
  light: 1,
  standard: 1,
  heavy: 1.15,
  "very-heavy": 1.25,
};

function receptionsToHours(r: Receptions): number {
  if (r === "0") return 0;
  if (r === "1") return 0;
  if (r === "2") return 0.5;
  return 1;
}

function bathroomsToExtra(b: Bathrooms): number {
  if (b === "1") return 0;
  if (b === "2") return 1;
  if (b === "3") return 2;
  return 3;
}

function kitchenAdj(k: KitchenSize): number {
  if (k === "small") return 0;
  if (k === "medium") return 0.5;
  return 1;
}

function recommendedCleaners(hoursHigh: number): number {
  if (hoursHigh <= 4) return 1;
  if (hoursHigh <= 8) return 2;
  return 3;
}

export function calculateQuote(input: QuoteInput): QuoteResult {
  const disclaimer =
    "This is an instant estimate based on the details provided. Final price may change if the property condition, access, size, or required scope differs from the information submitted. We will confirm the final price before the clean begins.";

  const priceToConfirm: string[] = [];
  const notes: string[] = [];
  const breakdown: QuoteResult["breakdown"] = [];

  // Commercial → custom quote, no price
  if (input.service === "commercial") {
    return {
      mode: "custom",
      low: 0,
      high: 0,
      hoursLow: 0,
      hoursHigh: 0,
      cleaners: 0,
      currency: "GBP",
      breakdown: [],
      priceToConfirm: ["Custom commercial quote — we'll build this around your site"],
      notes: [
        "For commercial premises we prepare a tailored quote based on site type, square footage, frequency, number of toilets and kitchen areas, and preferred cleaning times.",
      ],
      disclaimer,
    };
  }

  // Airbnb → from price by bedrooms
  if (input.service === "airbnb") {
    const base = AIRBNB_FROM[input.bedrooms];
    breakdown.push({ label: `Airbnb turnaround (${input.bedrooms} bed)`, amount: base });
    let total = base;
    for (const id of input.addons) {
      if (id !== "garage" && id !== "wheelie-bins") continue;
      const addon = ADDONS.find((a) => a.id === id);
      if (!addon) continue;
      const quantity = id === "wheelie-bins" ? Math.max(1, Math.floor(input.wheelieBins ?? 1)) : 1;
      const amount = addon.amount * quantity;
      total += amount;
      breakdown.push({ label: addon.label, amount, quantity, unitPrice: addon.amount });
    }
    priceToConfirm.push("Laundry / linen change — price to confirm");
    return {
      mode: "from",
      low: total,
      high: total,
      hoursLow: 1.5,
      hoursHigh: 3,
      cleaners: 1,
      currency: "GBP",
      breakdown,
      priceToConfirm,
      notes: ["Turnarounds are priced from the figure shown; final price agreed after we see the property."],
      disclaimer,
    };
  }

  // End of tenancy / military → fixed "from" price × condition
  if (input.service === "tenancy" || input.service === "military") {
    const table = input.service === "tenancy" ? TENANCY_FROM : MILITARY_FROM;
    const base = table[input.bedrooms];
    const extraBaths = bathroomsToExtra(input.bathrooms);
    const bathroomCharge = extraBaths * 20;
    const mult = CONDITION_MULT[input.condition];
    let low = base * mult + bathroomCharge;
    let high = base * mult + bathroomCharge;

    breakdown.push({
      label: `${input.service === "tenancy" ? "End of tenancy" : "Military march-out"} (${input.bedrooms} bed)`,
      amount: base + bathroomCharge,
    });
    breakdown.push({ label: "Inside windows", note: "Included" });
    breakdown.push({ label: "Inside cupboards", note: "Included" });
    if (mult > 1) {
      breakdown.push({
        label: `Condition surcharge (${input.condition === "heavy" ? "+15%" : "+25%"})`,
        amount: base * (mult - 1),
      });
    }

    // Add-ons
    let pctBump = 0;
    for (const id of input.addons) {
      if (id === "cupboards") continue;
      const a = ADDONS.find((x) => x.id === id);
      if (!a) continue;
      if ("ptc" in a && a.ptc) {
        const carpetTotal = a.id === "carpets" ? carpetSelectionTotal(input.carpetRooms ?? {}) : 0;
        if (a.id === "carpets" && carpetTotal > 0) {
          low += carpetTotal;
          high += carpetTotal;
          breakdown.push({ label: `Carpet cleaning (${carpetSelectionLabel(input.carpetRooms ?? {})})`, amount: carpetTotal });
        } else {
          priceToConfirm.push(a.label);
        }
        continue;
      }
      if ("pct" in a && a.pct) {
        pctBump += a.pct;
        breakdown.push({ label: a.label, note: "+20% applied" });
        continue;
      }
      if (a.amount) {
        const quantity = id === "wheelie-bins" ? Math.max(1, Math.floor(input.wheelieBins ?? 1)) : 1;
        const amount = a.amount * quantity;
        low += amount;
        high += amount;
        breakdown.push({ label: a.label, amount, quantity, unitPrice: a.amount });
      }
    }
    if (pctBump > 0) {
      low *= 1 + pctBump;
      high *= 1 + pctBump;
    }

    // Operational estimates are retained for the admin dashboard only.
    const baseHours = 4;
    const estHoursHigh = baseHours + extraBaths * 0.5 + 2 + (mult - 1) * 4;

    return {
      mode: "from",
      low: Math.round(low),
      high: Math.round(high),
      hoursLow: baseHours + extraBaths * 0.5,
      hoursHigh: Number(estHoursHigh.toFixed(1)),
      cleaners: recommendedCleaners(estHoursHigh),
      currency: "GBP",
      breakdown,
      priceToConfirm,
      notes: ["Fixed 'from' pricing — final agreed once we've seen access, size and condition."],
      disclaimer,
    };
  }

  // Hourly: regular, one-off, deep
  const rate = HOURLY_RATES[input.service];
  const table = input.service === "deep" ? DEEP_HOURS : REGULAR_HOURS;
  let [hLow, hHigh] = table[input.bedrooms];

  // Adjustments
  const extraBaths = bathroomsToExtra(input.bathrooms);
  hLow += extraBaths * 0.5;
  hHigh += extraBaths * 0.5;

  const recAdj = receptionsToHours(input.receptions);
  hLow += recAdj;
  hHigh += recAdj;

  const kAdj = kitchenAdj(input.kitchen);
  hLow += kAdj;
  hHigh += kAdj;

  // Condition — for hourly, expand the top of range
  if (input.condition === "heavy") hHigh *= 1.15;
  if (input.condition === "very-heavy") hHigh *= 1.25;

  // Minimum 2h
  if (hLow < 2) hLow = 2;
  if (hHigh < hLow) hHigh = hLow;

  let low = hLow * rate;
  let high = hHigh * rate;
  breakdown.push({
    label: input.service === "regular" ? "Regular domestic" : input.service === "deep" ? "Deep clean" : "One-off standard",
    amount: Math.round(high),
  });

  // Add-ons
  let pctBump = 0;
  for (const id of input.addons) {
    const a = ADDONS.find((x) => x.id === id);
    if (!a) continue;
    if ("ptc" in a && a.ptc) {
      const carpetTotal = a.id === "carpets" ? carpetSelectionTotal(input.carpetRooms ?? {}) : 0;
      if (a.id === "carpets" && carpetTotal > 0) {
        low += carpetTotal;
        high += carpetTotal;
        breakdown.push({ label: `Carpet cleaning (${carpetSelectionLabel(input.carpetRooms ?? {})})`, amount: carpetTotal });
      } else {
        priceToConfirm.push(a.label);
      }
      continue;
    }
    if ("pct" in a && a.pct) {
      pctBump += a.pct;
      breakdown.push({ label: a.label, note: "+20% applied" });
      continue;
    }
    if (a.amount) {
      const quantity = id === "wheelie-bins" ? Math.max(1, Math.floor(input.wheelieBins ?? 1)) : 1;
      const amount = a.amount * quantity;
      low += amount;
      high += amount;
      breakdown.push({ label: a.label, amount, quantity, unitPrice: a.amount });
    }
  }
  if (pctBump > 0) {
    low *= 1 + pctBump;
    high *= 1 + pctBump;
  }

  if (input.service === "regular" && (input.frequency === "weekly" || input.frequency === "fortnightly")) {
    notes.push("Regular slot pricing — same trusted cleaner where possible.");
  }

  return {
    mode: "estimate",
    low: Math.round(low),
    high: Math.round(high),
    hoursLow: Number(hLow.toFixed(1)),
    hoursHigh: Number(hHigh.toFixed(1)),
    cleaners: recommendedCleaners(hHigh),
    currency: "GBP",
    breakdown,
    priceToConfirm,
    notes,
    disclaimer,
  };
}

export function formatPrice(n: number): string {
  return `£${n.toLocaleString("en-GB")}`;
}

export function formatRange(r: QuoteResult): string {
  if (r.mode === "custom") return "Custom quote";
  return `${r.mode === "from" ? "from " : ""}${formatPrice(r.high)}`;
}

export function summariseForWhatsApp(input: QuoteInput, result: QuoteResult): string {
  const svc = SERVICES.find((s) => s.id === input.service)?.label ?? input.service;
  const lines = [
    `Hi IZHUB, I'd like to book a clean.`,
    ``,
    `Service: ${svc}`,
    `Bedrooms: ${input.bedrooms}   Bathrooms: ${input.bathrooms}`,
    `Reception rooms: ${input.receptions}   Kitchen: ${input.kitchen}`,
    `Condition: ${input.condition}   Frequency: ${input.frequency}`,
  ];
  if (input.addons.length) {
    lines.push(
      `Add-ons: ${input.addons
        .map((a) => ADDONS.find((x) => x.id === a)?.label ?? a)
        .join(", ")}`,
    );
  }
  lines.push("");
  if (input.addons.includes("wheelie-bins")) lines.push(`Wheelie bins: ${input.wheelieBins ?? 1}`);
  if (result.mode === "custom") {
    lines.push(`Estimate: Custom commercial quote — please contact me to discuss.`);
  } else {
    lines.push(`Estimate: ${formatRange(result)}`);
  }
  if (result.priceToConfirm.length) {
    lines.push(`Price to confirm: ${result.priceToConfirm.join(", ")}`);
  }
  return lines.join("\n");
}
